import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useRoleLabel } from "../contexts/AuthContext";
import { usePausableInterval } from "../hooks/usePausableInterval";
import { Button } from "../components/ui/button";
import BackButton from "../components/BackButton";
import DeployStatusBadge from "../components/DeployStatusBadge"; // Vincent B871 — badge sync preview↔PROD
import TagSearchBar from "../components/TagSearchBar"; // Vincent B904 — recherche par tags
import SilentIncidentBanner from "../components/admin/SilentIncidentBanner"; // Tâche 1148
import AiKeyFailureBanner from "../components/admin/AiKeyFailureBanner"; // Tâche 1318
import axios from "axios";
import { 
  House, 
  Upload, 
  Brain, 
  ChatCircle, 
  ChatCircleDots,
  GearSix, 
  SignOut,
  List,
  X,
  ClipboardText,
  ChartLine,
  Bell,
  MagnifyingGlass,
  // Vincent B915 (Eric 2026-02-14) — Icône Fire pour « Maintenance Fireflies ».
  Fire,
  CaretLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  BookOpen,
  Exam,
  Path,
  Handshake,
  Trophy,
  Moon,
  Sun,
  Sparkle,
  Certificate,
  Buildings,
  ChartBar,
  Queue,
  FolderOpen,
  ShieldCheck,
  Stack,
  Gauge,
  Eye,
  Files,
  FileText,
  MagicWand,
  Bug,
  Compass,
  UsersThree,
  Stethoscope,
  Heartbeat,
  Lightbulb,
  ShieldWarning,
  IdentificationCard,
  // Vincent (Eric 2026-05-15) — Icones supplémentaires utilisées dans la
  // construction du menu (4 piliers). L'absence de ces imports provoquait un
  // runtime ReferenceError au rendu de DashboardLayout (cause de l'erreur
  // « FileText is not defined » remontee dans client_errors mp6qrheq).
  Robot,
  Microphone,
  Crown,
  UserCirclePlus,
  Question,
  ClipboardText as AuditIcon,
  Lightning as FlagIcon,
  Database,
  CloudArrowUp,
  Trash,
  Envelope,
  Warning,
  // Vincent B953 hotfix — icones utilisées comme fallback dans menuConfig
  // (Fusion locuteurs B940 et Corrections factuelles B946). Sans ces
  // imports, le validate-menu.js CI-side bloque le deploy (React #130).
  User,
  CheckCircle,
  // Vincent B592 (Eric 2026-07-28) — Icône pour Ressources externes.
  Link as LinkIcon,
  // Vincent B702 (Eric 2026-02-11) — Icône Note pour l'entrée "Synthèses"
  // du menu latéral (parité avec l'onglet Synthèse de la fiche vidéo).
  Note,
  // Vincent B877 (Eric 2026-02-14) — Icône Heart pour l'item « Mes favoris »
  // (page globale du menu Apprentissage).
  Heart,
  // Vincent B989 hotfix deploy (Eric 2026-02-26) — Icône Play utilisée comme
  // fallback dans menuConfig.js:270 (`icons.ClipboardText || icons.Play`).
  // Sans l'import, le validateur build échoue avec React #130 potentiel.
  Play,
  // Tâche 1443 (Eric 2026-10-02) — Icône de l'entrée « Orthographe ».
  TextAa,
} from "@phosphor-icons/react";
import NotificationBell from "../components/NotificationBell";
import GlobalSearch from "../components/GlobalSearch";
import CommandPalette from "../components/CommandPalette";  // Tâche 1287 — Ctrl+K
import { useActionHighlight } from "../hooks/useActionHighlight";  // Tâche 1287
import OrgSwitcher from "../components/OrgSwitcher";
import RoleSwitcher from "../components/RoleSwitcher";
// Vincent P2.c (Eric 2026-07-31 SPEC STRICK PROD) — ErrorBoundary INTERNE
// autour du <Outlet />. Une page cassée n'écroule plus toute la navigation :
// le sidebar/topbar restent visibles et l'utilisateur peut cliquer une autre
// page pour continuer à travailler. Reset auto sur changement de route.
import RouteErrorBoundary from "../components/RouteErrorBoundary";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { buildMenuConfig, getEffectiveRole, filterMenuForRole, ROLE_FONDATEUR } from "../config/menuConfig";
import { canAccessAdmin, canAccessPlatform } from "../utils/permissions";
import { API_BASE as API } from "../lib/apiBase";
import { getLayoutBootstrap, isLayoutBootWindow } from "../lib/layoutBootstrap"; // B1099
// Vincent B702 (Eric 2026-02-11) — Palette timeline vidéo utilisée pour
// colorer les libellés du menu latéral (FAQ, Scripts, Procédures, etc.).
import { TYPE_COLORS } from "../lib/videoDetailFormat";

// Vincent bug #94 (2026-05-08) — Liste des routes accessibles directement depuis le menu de gauche.
// Les routes hors de cette liste verront un bouton « Retour » universel injecté en haut de page.
// Nota: les routes dynamiques (/dashboard/videos/:id, /dashboard/agent/drafts/:id) tombent toujours dans
// le else et reçoivent le bouton — c'est l'effet recherché.
const MENU_PATHS = new Set([
  "/dashboard",
  "/dashboard/upload",
  "/dashboard/external-resources",
  "/dashboard/favorites",
  "/dashboard/knowledge",
  "/dashboard/chat",
  "/dashboard/quiz",
  "/dashboard/onboarding",
  "/dashboard/onboarding-design",
  "/dashboard/leaderboard",
  "/dashboard/billing",
  "/dashboard/team-analytics",
  "/dashboard/certifications",
  "/dashboard/data-validation",
  "/dashboard/agent",
  "/dashboard/agent/drafts",
  "/dashboard/agora",
  "/dashboard/ai-video",
  "/dashboard/amf-compliance",
  "/dashboard/categories",
  "/dashboard/concepteur/onboarding",
  "/dashboard/dictionary",
  "/dashboard/discovery",
  "/dashboard/explorer",
  "/dashboard/learning-path",
  "/dashboard/privacy",
  "/dashboard/procedures",
  "/dashboard/scripts",
  "/dashboard/speakers",
  "/dashboard/video-queue",
  "/dashboard/admin/analytics",
  "/dashboard/admin/compliance-scoring",
  "/dashboard/admin/role-labels",
  "/dashboard/admin/firewall",
  "/dashboard/admin/jargon",
  "/dashboard/admin/jargon/enrichment",
  "/dashboard/admin/jargon/journal",
  "/dashboard/orthographe",
  "/dashboard/admin/stopwords",
  "/dashboard/admin/aggressive-dedup",
  "/dashboard/admin/cache-inspector",
  "/dashboard/admin/embeddings",
  "/dashboard/admin/partners",
  "/dashboard/admin/review",
  "/dashboard/admin/tech-stack",
  "/dashboard/admin/tiers",
  "/dashboard/settings/ai-package",
  "/dashboard/ai-package",
  "/dashboard/platform/approvals",
  "/dashboard/platform/audit-logs",
  "/dashboard/platform/feature-flags",
  "/dashboard/platform/overview",
  "/dashboard/platform/byok-overview",
  "/dashboard/admin/migrations",
  "/dashboard/admin/upload-maintenance",
]);

function DashboardBackButtonInjector() {
  const location = useLocation();
  // Normalize the pathname (strip trailing slash) and ignore query/hash.
  const path = (location.pathname || "/dashboard").replace(/\/$/, "") || "/dashboard";
  const isMenuRoute = MENU_PATHS.has(path);
  if (isMenuRoute) return null;
  // Vincent B725 v3 (Eric 2026-02-14) — Sur la page détail d'une vidéo
  // (`/dashboard/videos/:id`), le composant `VideoDetailHeader` porte déjà
  // son propre bouton « Retour » sur la MÊME ligne que Précédent / Suivant /
  // Importer / Partager. Injecter ici un second `<BackButton />` le pousse
  // fatalement sur une ligne à part au-dessus du toolbar (bug B725 rapporté
  // 3× par Vincent). On saute donc l'injection uniquement pour ces routes ;
  // VideoDetailHeader gère la responsabilité « Retour » à leur place.
  if (/^\/dashboard\/videos?\/[^/]+/.test(path)) return null;
  return (
    <div className="mb-3">
      <BackButton />
    </div>
  );
}

// Vincent batch v8 (Eric 2026-05-08) — Architecture rôle-based 4 piliers.
// Ancien système `tier` 1-5 conservé pour compatibilité simulation/RoleSwitcher.

// Tier system:
//   1 = Nouveau employé (~30%)  : Discovery, Dashboard, Chat, Quiz
//   2 = Employé formé (~45%)    : + Knowledge, Categories, Agora, Certifications
//   3 = Employé avancé (~65%)   : + Upload, Explorer, More menu
//   4 = Admin (~85%)            : + Admin section (review, queue, analytics, scoring)
//   5 = CEO/Directeur/Dev (100%): + Agent Knoria, Validation rapide, Platform KNORIA
//
// Items WITHOUT a `tier` are visible to all (tier 1+).
const getNavItems = (t, lang) => [
  { path: "/dashboard", icon: House, label: t("dashboard"), exact: true, tier: 1 },
  { path: "/dashboard/discovery", icon: Compass, label: lang === "en" ? "Discovery" : "Découverte", tier: 1, highlight: true, highlightColor: "amber" },
  { path: "/dashboard/agent", icon: Robot, label: lang === "en" ? "Knoria Agent" : "Agent Knoria", highlight: true, tier: 5 },
  { path: "/dashboard/agent/drafts", icon: MagicWand, label: lang === "en" ? "Quick Validation" : "Validation rapide", adminOnly: true, tier: 5 },
  { path: "/dashboard/chat", icon: ChatCircle, label: t("ask_knoria"), tier: 1 },
  { path: "/dashboard/quiz", icon: Exam, label: t("quiz"), tier: 1 },
  { path: "/dashboard/knowledge", icon: Brain, label: t("knowledge_base"), tier: 2 },
  { path: "/dashboard/categories", icon: FolderOpen, label: t("categories"), tier: 2 },
  { path: "/dashboard/certifications", icon: Certificate, label: t("certifications"), tier: 2 },
  { path: "/dashboard/agora", icon: ChatCircleDots, label: lang === "en" ? "The Agora" : "L'Agora", tier: 2 },
  { path: "/dashboard/upload", icon: Upload, label: t("upload_video"), tier: 3 },
  { path: "/dashboard/explorer", icon: Files, label: lang === "en" ? "File Explorer" : "Explorateur", tier: 3 },
  // Vincent B909 (Eric 2026-02-14 SPEC STRICK PROD) — Entrée sidebar
  // « 🔎 Recherche par tags » pour accéder à la page complète sans
  // devoir presser Entrée depuis la barre en tête de sidebar.
  { path: "/dashboard/search", icon: MagnifyingGlass, label: lang === "en" ? "Search by tags" : "Recherche par tags", tier: 3 },
  { path: "/dashboard/scripts", icon: ChatCircle, label: lang === "en" ? "Sales Scripts" : "Scripts de vente", tier: 3 },
];

const getAdminItems = (t, lang) => [
  { path: "/dashboard/admin/tiers", icon: UsersThree, label: lang === "en" ? "Tier Management" : "Gestion des tiers" },
  { path: "/dashboard/admin/role-labels", icon: UsersThree, label: lang === "en" ? "Role Management" : "Gestion des rôles" },
  { path: "/dashboard/video-queue", icon: Queue, label: t("video_queue") },
  { path: "/dashboard/speakers", icon: Microphone, label: lang === "en" ? "Speakers" : "Locuteurs" },
  { path: "/dashboard/admin/review", icon: ClipboardText, label: t("review_queue") },
  { path: "/dashboard/admin/compliance-scoring", icon: Gauge, label: t("scoring_amf") },
  { path: "/dashboard/admin/analytics", icon: ChartLine, label: t("analytics") },
  { path: "/dashboard/team-analytics", icon: ChartBar, label: t("team_analytics") },
  { path: "/dashboard/billing", icon: Buildings, label: t("enterprise") },
  { path: "/dashboard/admin/firewall", icon: ShieldCheck, label: t("firewall_ai") },
  { path: "/dashboard/admin/tech-stack", icon: Stack, label: t("tech_stack") },
];

const getMoreItems = (t, lang) => [
  { path: "/dashboard/amf-compliance", icon: ShieldCheck, label: t("amf_compliance") },
  { path: "/dashboard/onboarding", icon: Path, label: t("onboarding") },
  { path: "/dashboard/leaderboard", icon: Trophy, label: t("leaderboard") },
  { path: "/dashboard/ai-video", icon: Sparkle, label: lang === "en" ? "AI Video" : "Vidéo IA" },
  { path: "/dashboard/admin/jargon", icon: BookOpen, label: t("dictionary") },
  // Tâches 1315 · 1316 (Eric 2026-06-30) — Enrichissement en lot et journal,
  // rangés juste sous le Dictionnaire : ce sont deux vues du même objet.
  { path: "/dashboard/admin/jargon/enrichment", icon: BookOpen, label: lang === "en" ? "Batch enrichment" : "Enrichissement en lot" },
  { path: "/dashboard/admin/jargon/journal", icon: BookOpen, label: lang === "en" ? "Dictionary journal" : "Journal du dictionnaire" },
  // Tâche 1443 (Eric 2026-10-02) — « Orthographe », rangée avec le Dictionnaire
  // à la demande d'Eric : c'est la même famille d'objets (le vocabulaire du
  // cabinet). Concepteur / admin / fondateur uniquement.
  { path: "/dashboard/orthographe", icon: TextAa, label: lang === "en" ? "Spelling" : "Orthographe", designerOnly: true },
  // Vincent B967 (Eric 2026-02-14 SPEC STRICK PROD) — Ecran de gestion des
  // termes rejetes par le cleanup B961 (permet de restaurer les faux positifs).
  { path: "/dashboard/admin/dict-rejected", icon: BookOpen, label: lang === "en" ? "Rejected terms" : "Termes rejetés" },
  // Vincent B963 (Eric 2026-02-14 SPEC STRICK PROD) — Page Tags autonome
  // avec recherche + multi-select + bulk-merge (le choix de la version
  // canonique est laisse au user, cf. B962).
  { path: "/dashboard/tags", icon: BookOpen, label: lang === "en" ? "Tags" : "Tags" },
  { path: "/dashboard/admin/stopwords", icon: BookOpen, label: lang === "en" ? "Jargon stop-list" : "Stop-liste jargon" },
  // Vincent B784 (Eric 2026-02-18 SPEC STRICK PROD) — Batch detect legacy
  // aggressive_dedup + rerun en 1 clic pour récupérer les transcriptions
  // Whisper trop taillées par le filtre anti-hallucination.
  { path: "/dashboard/admin/aggressive-dedup", icon: BookOpen, label: lang === "en" ? "Aggressive dedup batch" : "Batch dedup agressif" },
  { path: "/dashboard/admin/cache-inspector", icon: BookOpen, label: lang === "en" ? "Cache inspector" : "Inspecteur du cache" },
  // Vincent B823 (Eric 2026-02-13) — Admin embeddings / RAG.
  { path: "/dashboard/admin/embeddings", icon: BookOpen, label: lang === "en" ? "Embeddings & RAG" : "Embeddings & RAG" },
  { path: "/dashboard/admin/partners", icon: Handshake, label: t("partners") },
];

const getPlatformItems = (t, lang) => [
  { path: "/dashboard/data-validation", icon: Eye, label: lang === "en" ? "Data Validation" : "Validation des données" },
  { path: "/dashboard/platform/overview", icon: Crown, label: t("overview") },
  { path: "/dashboard/platform/byok-overview", icon: Buildings, label: lang === "en" ? "BYOK Overview" : "Vue Forfait AI" },
  { path: "/dashboard/platform/approvals", icon: UserCirclePlus, label: lang === "en" ? "Approvals" : "Approbations" },
  { path: "/dashboard/privacy", icon: ShieldCheck, label: lang === "en" ? "Law 25 / Privacy" : "Loi 25 / Données" },
  { path: "/dashboard/platform/audit-logs", icon: AuditIcon, label: t("audit_logs") },
  { path: "/dashboard/platform/feature-flags", icon: FlagIcon, label: t("feature_flags") },
  { path: "/dashboard/admin/migrations", icon: Database, label: lang === "en" ? "Migrations & Backfill" : "Migrations & Backfill" },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [agoraUnread, setAgoraUnread] = useState(0);
  const [draftsCount, setDraftsCount] = useState(0);
  // Vincent B952 (Eric 2026-02-14 SPEC STRICK PROD) — Badge sur l'entrée
  // menu "Corrections factuelles" quand des jobs bulk-verify-factual ont
  // terminé APRÈS la dernière visite Vincent de la page.
  const [factualCorrectionsCount, setFactualCorrectionsCount] = useState(0);
  // Vincent #198 (Eric 2026-05-28) — Items du menu cachés par l'admin pour le rôle courant
  const [hiddenPaths, setHiddenPaths] = useState(new Set());
  const prevAgoraUnreadRef = useRef(0);
  const notifAudioRef = useRef(null);
  const { user, logout } = useAuth();
  const roleLabel = useRoleLabel();
  const { theme, toggleTheme } = useTheme();
  const { lang, switchLang, t } = useLang();
  // Tâche 1287 — Surligne le bouton visé par une action lancée depuis Ctrl+K.
  useActionHighlight();
  const navigate = useNavigate();

  // Eric (2026-05-12) — Préserve la position de scroll du menu de gauche entre
  // les navigations. Avant, le menu remontait en haut à chaque clic sur un item
  // car le composant se ré-rendait. On sauvegarde la position dans une ref +
  // sessionStorage et on la restore juste après chaque re-mount.
  const desktopNavRef = useRef(null);
  const mobileNavRef = useRef(null);
  const SCROLL_KEY = "knoria.sidebar.scroll";

  useEffect(() => {
    // Restore scroll position on mount AND after route change re-render
    const saved = parseInt(sessionStorage.getItem(SCROLL_KEY) || "0", 10);
    if (desktopNavRef.current && !isNaN(saved)) {
      desktopNavRef.current.scrollTop = saved;
    }
    if (mobileNavRef.current && !isNaN(saved)) {
      mobileNavRef.current.scrollTop = saved;
    }
  });

  const handleSidebarScroll = (e) => {
    try { sessionStorage.setItem(SCROLL_KEY, String(e.target.scrollTop)); } catch { /* sessionStorage indisponible (mode privé strict / quota) : le scroll ne sera pas restauré à la prochaine visite */ }
  };

  // Vincent B667 (Eric 2026-08-05 SPEC STRICK PROD) — Utilisation du hook
  // `usePausableInterval` qui centralise les 4 garde-fous (inFlight, hidden,
  // uploadActive, backoff progressif) auparavant dupliqués dans les 3
  // pollers. Les callbacks doivent retourner `false` pour signaler un échec
  // (déclenche le backoff), `true` ou `undefined` pour succès.
  //
  // Vincent B957 (Eric 2026-02-14 SPEC STRICK PROD) — REFONTE : les 4
  // pollers (pending, agora, drafts, factual-corrections) sont maintenant
  // UNIFIÉS derrière un unique poll `/api/dashboard/summary` toutes les
  // 30 s. Sous charge PROD (Tier 0 pod), 4 requêtes parallèles saturaient
  // le pool MongoDB (`maxPoolSize=50`) et déclenchaient
  // `WaitQueueTimeoutError`. Le nouveau endpoint effectue les 4
  // count_documents en série (1 seul connect slot occupé à la fois).
  const fetchDashboardSummary = useCallback(async () => {
    if (!user) return true;
    try {
      // B1099 (Eric 2026-09-07) — Le PREMIER chargement des badges vient de
      // l'appel agrégé du gabarit (`/api/layout/bootstrap`) ; les sondes
      // suivantes (30 s) gardent `/api/dashboard/summary`, plus léger.
      let bootData = null;
      if (isLayoutBootWindow()) {
        try {
          const boot = await getLayoutBootstrap();
          bootData = boot?.summary || null;
        } catch { /* on retombe sur l'appel dédié juste en dessous */ }
      }
      // Vincent B977 (Eric 2026-02-26 SPEC STRICK PROD) — Passe la date
      // de derniere visite de la page Corrections factuelles pour que
      // le badge se comporte comme un compteur « unread » (seulement
      // les nouvelles corrections depuis la dernière visite).
      let factualSince = null;
      try {
        factualSince = localStorage.getItem("knoria_last_visited_factual_corrections");
      } catch { /* localStorage indisponible → badge continue en mode total */ }
      const url = factualSince
        ? `${API}/api/dashboard/summary?factual_since=${encodeURIComponent(factualSince)}`
        : `${API}/api/dashboard/summary`;
      const { data } = bootData
        ? { data: bootData }
        : await axios.get(url, { withCredentials: true });
      // Agora unread + son + notif (comportement historique préservé)
      const newAgora = data.agora_unread || 0;
      if (newAgora > prevAgoraUnreadRef.current && prevAgoraUnreadRef.current >= 0) {
        try {
          if (!notifAudioRef.current) notifAudioRef.current = new Audio("/notification.wav");
          notifAudioRef.current.play().catch(() => {});
        } catch { /* notification son facultatif : autoplay policy peut bloquer, on ignore silencieusement */ }
        if (Notification.permission === "granted") {
          new Notification(lang === "en" ? "The Agora — KNORIA" : "L'Agora — KNORIA", { body: `${newAgora} nouveau(x) message(s)`, icon: "/favicon.ico" });
        } else if (Notification.permission !== "denied") {
          Notification.requestPermission();
        }
      }
      prevAgoraUnreadRef.current = newAgora;
      setAgoraUnread(newAgora);
      // Admin-only compteurs (le backend omet la clé si non autorisé — on
      // fallback à 0 pour garder le badge invisible).
      setPendingCount(data.pending_users_count || 0);
      setDraftsCount(data.drafts_count || 0);
      setFactualCorrectionsCount(data.factual_corrections_count || 0);
      return true;
    } catch {
      return false;
    }
  }, [user, lang]);

  usePausableInterval(fetchDashboardSummary, 30000, { deps: [fetchDashboardSummary] });

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Compute user's effective access tier (1-5)
  // Eric 2026-06-25 (iter118 B3) — migré vers les helpers canoniques.
  // - canAccessPlatform → 5 (CEO/Directeur/Dev/Founder/Super-admin)
  // - canAccessAdmin    → 4 (Admin)
  // - access_level "avance"        → 3 (Employé avancé)
  // - access_level "intermediaire" → 2 (Employé formé)
  // - access_level "debutant" / default → 1 (Nouveau)
  const getUserTier = (u) => {
    if (!u) return 1;
    if (canAccessPlatform(u)) return 5;
    if (canAccessAdmin(u)) return 4;
    const lvl = u.access_level || "debutant";
    if (lvl === "avance") return 3;
    if (lvl === "intermediaire") return 2;
    return 1;
  };
  const userTier = getUserTier(user);

  // Vincent batch v8 (Eric 2026-05-08) — Architecture rôle-based 4 piliers (menuConfig.js).
  // Le système legacy navItems/adminItems/moreItems/platformItems n'est plus utilisé pour
  // le rendu mais reste construit pour compatibilité éventuelle. Le rendu utilise `pillars`.
  const _icons = {
    House, Compass, Robot, MagicWand, ChatCircle, Exam, Brain, FolderOpen, Certificate, ChatCircleDots,
    Upload, Files, FileText, Path, Trophy, ShieldCheck, BookOpen, Microphone, Sparkle, Eye, Queue, ClipboardText,
    Gauge, ChartLine, ChartBar, Buildings, Handshake, Stack, Bug, UsersThree, Crown, UserCirclePlus,
    AuditIcon, FlagIcon, Question, Stethoscope, Trash,
    // Vincent B592 — icône Ressources externes
    Link: LinkIcon,
    // 2026-05-24 hotfix React #130 — icones manquants utilisés dans menuConfig
    Lightbulb, ShieldWarning, IdentificationCard, Heartbeat,
    // 2026-06-03 hotfix — item 26 Sauvegardes (Database+CloudArrowUp utilisés dans menuConfig)
    Database, CloudArrowUp,
    // Vincent B953 hotfix (Eric 2026-02-14) — Icones fallback pour B940 et B946
    User, CheckCircle,
    // 2026-06-04 — Eric iter85b : Envelope pour menuConfig "Statut système Resend"
    Envelope,
    // BOGUE 553 (Eric 2026-07-25) — Warning pour menuConfig "Alertes pipeline"
    Warning,
    // Vincent B702 (Eric 2026-02-11) — Note pour "Synthèses" (menu KB)
    Note,
    // Vincent B877 (Eric 2026-02-14) — Heart pour « Mes favoris » (Apprentissage).
    Heart,
    // Vincent B909 (Eric 2026-02-14) — MagnifyingGlass pour « Recherche par tags »
    // dans « Structure de données ». Exposé au menuConfig.js via _icons.
    MagnifyingGlass,
    // Vincent B915 (Eric 2026-02-14) — Fire pour « Maintenance Fireflies »
    // dans « Admin > Technique ». Exposé au menuConfig.js via _icons.
    Fire,
    // Vincent B989 hotfix deploy (Eric 2026-02-26) — Play : fallback utilisé
    // par menuConfig.js:270 (`icons.ClipboardText || icons.Play`). Doit être
    // déclaré ici sinon validate-menu.js échoue le build.
    Play,
    // Tâche 1443 — TextAa pour « Orthographe » (menuConfig.js).
    TextAa,
  };
  const _effectiveRole = getEffectiveRole(user);

  // Vincent #198 (2026-05-28) → Eric 2026-06-25 (iter118 B3) — Charge la matrice
  // d'accès de l'org au montage et applique les overrides au menu. Les fondateurs
  // / équipe plateforme ne sont JAMAIS restreints (cross-org).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id || canAccessPlatform(user)) {
        setHiddenPaths(new Set());
        return;
      }
      try {
        const { data } = await axios.get(`${API}/api/org/access-matrix`, { withCredentials: true });
        if (cancelled) return;
        const overrides = data?.role_access_overrides || {};
        const myHidden = overrides[_effectiveRole] || [];
        setHiddenPaths(new Set(myHidden.map((p) => (p || "").replace(/\/$/, ""))));
      } catch {
        if (!cancelled) setHiddenPaths(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user, _effectiveRole]);
  const _injectBadges = (item) => {
    if (item.path === "/dashboard/agora") return { ...item, badge: agoraUnread };
    if (item.path === "/dashboard/agent/drafts") return { ...item, badge: draftsCount };
    if (item.path === "/dashboard/platform/approvals") return { ...item, badge: pendingCount };
    // Vincent B952 (Eric 2026-02-14) — Badge sur Corrections factuelles.
    if (item.path === "/dashboard/admin/factual-corrections") return { ...item, badge: factualCorrectionsCount };
    return item;
  };
  const pillars = filterMenuForRole(buildMenuConfig(_icons, t, lang), _effectiveRole, hiddenPaths)
    .map((p) => ({
      ...p,
      sections: p.sections.map((s) => ({ ...s, items: s.items.map(_injectBadges) })),
    }));

  // 2026-05-24 — Boot-time validation : empêche les crash React #130 silencieux.
  // Si un item du menu a icon=undefined, on logge UNE FOIS un avertissement clair
  // (le fallback NavItem -> Question affichera quand-même quelque chose).
  useEffect(() => {
    const missing = [];
    for (const p of pillars) {
      for (const s of (p.sections || [])) {
        for (const it of (s.items || [])) {
          if (!it.icon) missing.push(`${p.id} > ${s.id} > ${it.label} (${it.path})`);
        }
      }
    }
    if (missing.length > 0) {
      console.error(
        `[Menu Validator] ${missing.length} item(s) sans icône — ajoute-les dans la map _icons :\n` +
          missing.map((m) => `  • ${m}`).join("\n")
      );
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Vincent B863 (Eric 2026-02-14 SPEC STRICK PROD) — Extension B862 :
  // fonction (pas composant) pour éviter `react/no-unstable-nested-
  // components`. Chaque re-render du parent (ex. toggle d'accordéon,
  // frappe dans le champ recherche menu, changement de route)
  // recréait une nouvelle identité de type pour `NavItem` → React
  // démontait/remontait TOUS les items de la sidebar → coût de rendu
  // inutile + potentielle perte de focus/tooltips. Le `key` est
  // désormais un paramètre explicite injecté sur l'élément racine.
  const renderNavItem = (item, itemKey) => {
    // 2026-05-24 hotfix React #130 — Defensive fallback : si item.icon est undefined
    // (icone manquant dans la map _icons), on tombe sur Question pour éviter le crash global.
    const Icon = item.icon || Question;
    // Vincent batch v8 — items "À développer" rendus en grisé non-cliquables
    if (item.comingSoon) {
      return (
        <div
          key={itemKey}
          className={`sidebar-item relative flex items-center gap-3 px-3 py-2.5 rounded-sm text-zinc-400 cursor-not-allowed select-none opacity-60`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          title={sidebarOpen ? (lang === "en" ? "Coming soon" : "Bientôt disponible") : `${item.label} — ${lang === "en" ? "Coming soon" : "Bientôt"}`}
        >
          <Icon size={sidebarOpen ? 20 : 28} weight={sidebarOpen ? "regular" : "bold"} />
          {sidebarOpen && (
            <span className="text-sm flex-1 flex items-center justify-between">
              <span className="truncate">{item.label}</span>
              <span className="ml-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 shrink-0" data-testid={`coming-soon-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {lang === "en" ? "Soon" : "Bientôt"}
              </span>
            </span>
          )}
        </div>
      );
    }
    return (
    <NavLink
      key={itemKey}
      to={item.path}
      end={item.exact}
      onClick={() => setMobileMenuOpen(false)}
      // Vincent B702 (Eric 2026-02-11) — Applique la couleur type (FAQ bleu,
      // Scripts jaune, Procédures vert, Concepts indigo, Objections rouge,
      // Synthèses teal). Inline style écrase le `color: var(--knoria-brand-
      // primary)` de la classe `.sidebar-item.active` (sinon l'actif redevient
      // bleu marque). Actif = font-bold (poids), pas de changement de teinte.
      style={item.colorType && TYPE_COLORS[item.colorType]
        ? { color: TYPE_COLORS[item.colorType].color }
        : undefined}
      className={({ isActive }) => {
        // Vincent #204/#205/#206 (Eric 2026-05-28) — Quand plusieurs items partagent
        // le même path mais avec ?tab= différent (ex: Base de connaissances vs FAQ vs
        // Concepts vs Objections, tous sur /dashboard/knowledge), NavLink highlight
        // les DEUX simultanément. Custom logic : si le path de l'item contient `?tab=`,
        // l'item n'est actif QUE quand le tab de l'URL courante matche exactement.
        // Si l'item n'a pas de `?tab=`, il n'est actif QUE quand l'URL courante n'a
        // PAS de tab (sinon l'item parent "Base de connaissances" reste actif sur les
        // sous-tabs, ce qui est le bug exact rapporté).
        const itemUrl = new URL(item.path, window.location.origin);
        const itemTab = itemUrl.searchParams.get("tab");
        const currentTab = new URLSearchParams(window.location.search).get("tab");
        const pathMatches = window.location.pathname === itemUrl.pathname;
        let effectivelyActive;
        if (itemTab) {
          effectivelyActive = pathMatches && currentTab === itemTab;
        } else if (pathMatches && currentTab) {
          effectivelyActive = false;
        } else {
          effectivelyActive = isActive;
        }
        // Vincent B702 — Poids gras si actif ET colorType présent, sinon
        // comportement historique (couleur brand + weight 500 via CSS active).
        const weightClass = effectivelyActive && item.colorType ? "font-bold" : "";
        return `sidebar-item relative group flex items-center gap-3 px-3 py-2.5 rounded-sm ${weightClass} ${
          effectivelyActive ? "active" : "text-zinc-600 hover:text-zinc-900"
        }`;
      }}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      title={!sidebarOpen ? item.label : undefined}
    >
      {/* Vincent #403 (Eric 2026-07-14 SPEC STRICK — 5e passe / screenshot Vincent) —
          BUG confirmé par screenshot Vincent 14/07 : les tooltips custom étaient
          MASQUÉS car le <nav> parent a `overflow-y-auto` qui clip aussi le X.
          Fix double : (1) `title` HTML natif pour garantir un tooltip même
          quand le CSS custom est clippé (fallback OS/navigateur), (2) tooltip
          custom en `position: fixed` avec coordonnées calculées au hover pour
          échapper au clipping du parent. */}
      <Icon size={sidebarOpen ? 20 : 32} weight={sidebarOpen ? "regular" : "bold"} />
      {!sidebarOpen && (
        <span
          className="pointer-events-none fixed z-[9999] whitespace-nowrap px-3 py-1.5 rounded-md bg-zinc-900 text-white text-sm font-medium shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          style={{ left: "72px", transform: "translateY(-50%)", top: "var(--knoria-tooltip-y, 50%)" }}
          data-testid={`nav-tooltip-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          ref={(el) => {
            if (!el) return;
            const parent = el.parentElement;
            if (!parent) return;
            const setPos = () => {
              const rect = parent.getBoundingClientRect();
              el.style.setProperty("--knoria-tooltip-y", `${rect.top + rect.height / 2}px`);
            };
            parent.addEventListener("mouseenter", setPos);
          }}
        >
          {item.label}
        </span>
      )}
      {sidebarOpen && (
        <span className="text-sm flex-1 flex items-center justify-between">
          {item.label}
          {item.badge > 0 && (
            <span className="ml-auto min-w-[20px] h-5 flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 animate-pulse" data-testid={`badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              {item.badge > 9 ? "9+" : item.badge}
            </span>
          )}
        </span>
      )}
      {!sidebarOpen && item.badge > 0 && (
        <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
      )}
    </NavLink>
    );
  };

  // Vincent batch v8 — Render block for one pillar (header + sections + items).
  // Used in both mobile menu and desktop sidebar.
  // Vincent batch v8 (Eric/Vincent 2026-05-08) — Sections en accordéons.
  // Par défaut on n'ouvre QUE la section qui contient l'URL active. Les autres
  // sont fermées pour alléger le menu (Vincent QA feedback). Le user peut
  // ensuite ouvrir/fermer manuellement, et son choix est persisté en localStorage.
  const location = useLocation();
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = window.localStorage.getItem("knoria.sidebar.collapsed");
      return raw ? JSON.parse(raw) : null;
    } catch (_e) { return null; }
  });
  const activeSectionIds = useMemo(() => {
    const ids = new Set();
    for (const p of pillars) {
      for (const s of (p.sections || [])) {
        if ((s.items || []).some((i) => i.path && location.pathname.startsWith(i.path) && (i.exact ? location.pathname === i.path : true))) {
          ids.add(s.id);
        }
      }
    }
    return ids;
  }, [pillars, location.pathname]);

  // Sprint 2 polish (2026-05-22) — Track which "isNew" sections have already been clicked/toggled by the user.
  // Once seen, the NOUVEAU badge disappears for that section. Persisted in localStorage so it sticks across reloads.
  const [seenNewSections, setSeenNewSections] = useState(() => {
    try {
      const raw = window.localStorage.getItem("knoria.sidebar.seenNew");
      return raw ? JSON.parse(raw) : [];
    } catch (_e) { return []; }
  });

  // If user never set a state, auto-collapse all except active sections.
  // We compute the effective state on-the-fly for first render.
  const isSectionCollapsed = useCallback((sectionId) => {
    if (collapsedSections && Object.prototype.hasOwnProperty.call(collapsedSections, sectionId)) {
      return !!collapsedSections[sectionId];
    }
    // Default: collapsed unless active
    return !activeSectionIds.has(sectionId);
  }, [collapsedSections, activeSectionIds]);

  const toggleSection = useCallback((sectionId) => {
    // Sprint 2 polish (2026-05-22) — Tag section as "seen" so the NOUVEAU badge disappears
    setSeenNewSections((prev) => {
      if (prev.includes(sectionId)) return prev;
      const next = [...prev, sectionId];
      try { window.localStorage.setItem("knoria.sidebar.seenNew", JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
    setCollapsedSections((prev) => {
      const baseline = {};
      // Seed with current "default" state so toggle is consistent
      for (const p of pillars) for (const s of (p.sections || [])) {
        baseline[s.id] = !activeSectionIds.has(s.id);
      }
      const next = { ...baseline, ...(prev || {}) };
      next[sectionId] = !next[sectionId];
      try { window.localStorage.setItem("knoria.sidebar.collapsed", JSON.stringify(next)); } catch (_e) { /* ignore */ }
      return next;
    });
  }, [pillars, activeSectionIds]);

  // Vincent batch v8 + Eric request 2026-05-08 — Search + Expand/Collapse all
  const [menuSearch, setMenuSearch] = useState("");
  const lcSearch = menuSearch.trim().toLowerCase();
  const filteredPillars = useMemo(() => {
    if (!lcSearch) return pillars;
    return pillars
      .map((p) => ({
        ...p,
        sections: p.sections
          .map((s) => ({ ...s, items: s.items.filter((i) => (i.label || "").toLowerCase().includes(lcSearch)) }))
          .filter((s) => s.items.length > 0),
      }))
      .filter((p) => p.sections.length > 0);
  }, [pillars, lcSearch]);

  const expandAll = useCallback(() => {
    const next = {};
    for (const p of pillars) for (const s of (p.sections || [])) next[s.id] = false;
    setCollapsedSections(next);
    try { window.localStorage.setItem("knoria.sidebar.collapsed", JSON.stringify(next)); } catch (_e) { /* ignore */ }
  }, [pillars]);
  const collapseAll = useCallback(() => {
    const next = {};
    for (const p of pillars) for (const s of (p.sections || [])) next[s.id] = true;
    setCollapsedSections(next);
    try { window.localStorage.setItem("knoria.sidebar.collapsed", JSON.stringify(next)); } catch (_e) { /* ignore */ }
  }, [pillars]);
  // "Heuristic": if every section is currently collapsed, the button label is "Tout déplier",
  // otherwise "Tout replier". Computed off live state.
  const allCollapsed = useMemo(() => {
    for (const p of pillars) for (const s of (p.sections || [])) {
      if (!isSectionCollapsed(s.id)) return false;
    }
    return true;
  }, [pillars, isSectionCollapsed]);

  // Vincent B862 (Eric 2026-02-14 SPEC STRICK PROD) — Rendu du toolbar
  // sidebar en FONCTION (pas composant React) pour éviter la perte de
  // focus sur le champ « Rechercher dans le menu ». Anti-pattern
  // `react/no-unstable-nested-components` : un composant défini dans
  // le corps du parent devient une nouvelle identité de type à
  // chaque re-render → React démonte/remonte l'input → focus perdu à
  // chaque frappe. En passant à une simple fonction qui retourne du
  // JSX (appelée avec `()` au lieu de `<>`), React voit directement
  // la `<div>` racine dont le type ne change jamais entre renders.
  const renderSidebarSearchToolbar = () => {
    if (!sidebarOpen) return null; // Hide entirely when sidebar is collapsed
    return (
      <div className="px-1 pt-1 pb-2 mb-1 border-b border-zinc-100/80" data-testid="sidebar-search-toolbar">
        <div className="relative mb-1.5">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={menuSearch}
            onChange={(e) => setMenuSearch(e.target.value)}
            placeholder={lang === "fr" ? "Rechercher dans le menu..." : "Search the menu..."}
            className="w-full text-xs pl-7 pr-7 py-1.5 rounded-md border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]/30 outline-none transition-all placeholder:text-zinc-400"
            data-testid="sidebar-search-input"
          />
          {menuSearch && (
            <button
              type="button"
              onClick={() => setMenuSearch("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700"
              data-testid="sidebar-search-clear"
              aria-label={lang === "fr" ? "Effacer" : "Clear"}
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
        {/* Vincent B904 (Eric 2026-02-14 SPEC STRICK PROD) — Barre de
            recherche par TAGS dans la sidebar (visible partout). Autocomplete
            + résultats cliquables → navigate vers la vidéo/texte. */}
        <div className="mb-1.5" data-testid="sidebar-tag-search-wrapper">
          <TagSearchBar compact />
        </div>
        <button
          type="button"
          onClick={allCollapsed ? expandAll : collapseAll}
          className="w-full text-[10px] uppercase tracking-wide font-semibold text-zinc-500 hover:text-[#002FA7] py-1 rounded transition-colors flex items-center justify-center gap-1.5"
          data-testid="sidebar-toggle-all-btn"
        >
          {allCollapsed ? (
            <>
              <CaretDown size={9} weight="bold" />
              {lang === "fr" ? "Tout déplier" : "Expand all"}
            </>
          ) : (
            <>
              <CaretUp size={9} weight="bold" />
              {lang === "fr" ? "Tout replier" : "Collapse all"}
            </>
          )}
        </button>
      </div>
    );
  };

  const renderPillarBlock = (pillar, index, forceExpand = false) => {
    // Vincent batch v8 (Eric 2026-05-08) — accordéon activé même pour les piliers
    // à 1 seule section (ex: Plateforme Knoria). Dans ce cas, le label du PILIER
    // devient lui-même le toggle (et on n'affiche pas le label de section dupliqué).
    // Vincent B863 (Eric 2026-02-14 SPEC STRICK PROD) — Converti en
    // fonction (renderPillarBlock) pour éviter
    // `react/no-unstable-nested-components` (cf. B862). Le `key`
    // reste sur le `<div>` racine (`key={pillar.id}` ligne suivante).
    const isSinglePillar = sidebarOpen && pillar.sections.length === 1;
    const singleSection = isSinglePillar ? pillar.sections[0] : null;
    const singleCollapsed = singleSection
      ? (forceExpand ? false : isSectionCollapsed(singleSection.id))
      : false;
    return (
    <div key={pillar.id} data-testid={`pillar-${pillar.id}`} className={index > 0 ? "mt-4" : ""}>
      <div className={`pt-3 pb-1.5 ${sidebarOpen ? "" : "text-center"}`}>
        {sidebarOpen ? (
          isSinglePillar ? (
            <button
              type="button"
              onClick={() => toggleSection(singleSection.id)}
              className={`w-full flex items-center justify-between gap-1.5 hover:opacity-80 transition-opacity ${pillar.crown ? "text-cyan-400/80" : ""}`}
              data-testid={`pillar-toggle-${pillar.id}`}
              aria-expanded={!singleCollapsed}
            >
              <span className={`overline flex items-center gap-1.5 ${pillar.crown ? "text-cyan-400/80" : ""}`} data-testid={`pillar-label-${pillar.id}`}>
                {pillar.crown && <Crown size={12} weight="fill" className="text-cyan-400" />}
                {pillar.label}
              </span>
              <CaretDown
                size={10}
                weight="bold"
                className={`transition-transform duration-200 ${singleCollapsed ? "-rotate-90" : "rotate-0"} ${pillar.crown ? "text-cyan-400/80" : "text-zinc-400"}`}
              />
            </button>
          ) : (
            <span className={`overline flex items-center gap-1.5 ${pillar.crown ? "text-cyan-400/80" : ""}`} data-testid={`pillar-label-${pillar.id}`}>
              {pillar.crown && <Crown size={12} weight="fill" className="text-cyan-400" />}
              {pillar.label}
            </span>
          )
        ) : (
          <div className={`w-8 h-px mx-auto ${pillar.crown ? "bg-cyan-500/30" : "bg-zinc-200"}`} />
        )}
      </div>
      {pillar.sections.map((section) => {
        const collapsed = forceExpand ? false : (sidebarOpen && pillar.sections.length > 1 ? isSectionCollapsed(section.id) : (isSinglePillar ? singleCollapsed : false));
        const hasMultipleSections = sidebarOpen && pillar.sections.length > 1;
        const allItemsAlwaysVisible = (section.items || []).every((i) => i.alwaysVisible);
        // Vincent batch v8 (Eric 2026-05-08) — `hideLabel` retire le label de section
        // (utilisé pour les sections "Introduction" qui n'ont qu'un Tableau de bord).
        const showToggle = hasMultipleSections && !allItemsAlwaysVisible && !section.hideLabel;
        const showStaticLabel = hasMultipleSections && allItemsAlwaysVisible && !section.hideLabel;
        return (
          <div key={section.id} data-testid={`section-${section.id}`} className="mt-1">
            {showToggle && (
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className="w-full pl-3 pr-2 pt-2 pb-0.5 flex items-center justify-between text-[10px] uppercase tracking-wide text-zinc-500 hover:text-zinc-800 font-semibold transition-colors"
                data-testid={`section-toggle-${section.id}`}
                aria-expanded={!collapsed}
              >
                <span className="flex items-center gap-1.5">
                  <span data-testid={`section-label-${section.id}`}>{section.label}</span>
                  {section.isNew && !seenNewSections.includes(section.id) && (
                    <span
                      className="text-[8px] font-bold uppercase px-1 py-px rounded bg-teal-500 text-white tracking-wider"
                      data-testid={`section-new-badge-${section.id}`}
                      title={lang === "en" ? "New section" : "Nouvelle section"}
                    >
                      {lang === "en" ? "New" : "Nouveau"}
                    </span>
                  )}
                </span>
                <CaretDown
                  size={10}
                  weight="bold"
                  className={`transition-transform duration-200 ${collapsed ? "-rotate-90" : "rotate-0"}`}
                />
              </button>
            )}
            {showStaticLabel && (
              <div
                className="pl-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1.5"
                data-testid={`section-label-${section.id}`}
              >
                <span>{section.label}</span>
                {section.isNew && !seenNewSections.includes(section.id) && (
                  <span
                    className="text-[8px] font-bold uppercase px-1 py-px rounded bg-teal-500 text-white tracking-wider"
                    data-testid={`section-new-badge-${section.id}`}
                    title={lang === "en" ? "New section" : "Nouvelle section"}
                  >
                    {lang === "en" ? "New" : "Nouveau"}
                  </span>
                )}
              </div>
            )}
            {section.items.map((item) => {
              // alwaysVisible items render even when section is collapsed
              const isVisible = !collapsed || item.alwaysVisible;
              return isVisible ? renderNavItem(item, `${section.id}-${item.path}`) : null;
            })}
          </div>
        );
      })}
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Vincent B556 (Eric 2026-07-25) — Bandeau simulation TOUT en haut de
          la fenêtre, au-dessus de tous les headers (mobile z-50 + desktop z-30).
          Avant : `sticky top-14 z-20` sous les headers → cachait les menus
          importants du header. Après : `fixed top-0 z-[60]` toujours visible
          en haut, + décalage du contenu principal (`pt-8` sur main + header) via
          la classe `simulation-active` posée sur <body>. */}
      {user?.is_simulating && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] bg-amber-100 dark:bg-amber-950/70 border-b border-amber-400/70 dark:border-amber-700 text-amber-900 dark:text-amber-100 px-4 py-1.5 flex items-center justify-between gap-3 text-sm shadow-sm"
          data-testid="simulation-banner"
        >
          {/* Eric 2026-06-18 — Bandeau simulation épuré : on ne montre PLUS le
              rôle simulé ni le pourcentage estimé, pour ne pas influencer la
              configuration de la "Sélection des accès" depuis cette vue.
              Vincent B556 2026-07-25 — Placé au-dessus des headers. */}
          <div className="flex items-center gap-2">
            <Eye size={16} weight="fill" />
            <span className="font-semibold">{lang === "fr" ? "Mode simulation" : "Simulation mode"}</span>
          </div>
          <button
            onClick={async () => {
              try {
                await axios.post(`${API}/api/auth/view-as`, { role: null }, { withCredentials: true });
                window.location.reload();
              } catch (_e) { /* ignore */ }
            }}
            className="px-3 py-1 text-xs font-bold rounded-md bg-amber-900/10 hover:bg-amber-900/20 dark:bg-amber-100/10 dark:hover:bg-amber-100/20 whitespace-nowrap"
            data-testid="exit-simulation-btn"
          >
            {lang === "fr" ? "Quitter la simulation" : "Exit simulation"}
          </button>
        </div>
      )}

      {/* Mobile header */}
      <header className={`lg:hidden fixed left-0 right-0 h-14 bg-black border-b border-white/10 z-50 flex items-center justify-between px-4 ${user?.is_simulating ? "top-8" : "top-0"}`}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 hover:bg-white/10 rounded-sm text-white"
            data-testid="mobile-menu-btn"
          >
            <List size={24} />
          </button>
          <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-8 object-contain" />
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-white/10 rounded-sm text-white" data-testid="mobile-search-btn">
            <MagnifyingGlass size={20} />
          </button>
          <button className="p-2 hover:bg-white/10 rounded-sm text-white" data-testid="mobile-notifications-btn">
            <Bell size={20} />
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-card animate-fade-in flex flex-col">
            <div className="h-14 flex-shrink-0 flex items-center justify-between px-3 border-b border-border" style={{ backgroundColor: "#000000" }}>
              <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-9 object-contain" />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-white/10 rounded-sm text-white"
                data-testid="close-mobile-menu-btn"
              >
                <X size={20} />
              </button>
            </div>
            <nav ref={mobileNavRef} onScroll={handleSidebarScroll} className="flex-1 overflow-y-auto p-4 space-y-1 pb-2" data-testid="mobile-nav">
              {renderSidebarSearchToolbar()}
              {(lcSearch ? filteredPillars : pillars).map((p, i) => renderPillarBlock(p, i, !!lcSearch))}
              {lcSearch && filteredPillars.length === 0 && (
                <div className="text-center py-6 text-sm text-zinc-400" data-testid="menu-search-empty">
                  {lang === "fr" ? "Aucun résultat" : "No results"}
                </div>
              )}
            </nav>
            <div className="flex-shrink-0 p-4 border-t border-zinc-200 bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{user?.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full justify-start gap-2 text-zinc-600"
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
              >
                <SignOut size={18} />
                {t("sign_out")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 bg-card border-r border-border z-40 transition-all duration-200 ${
          sidebarOpen ? "w-60" : "w-16"
        }`}
      >
        <div data-testid="sidebar-logo" className="overflow-hidden" style={{ backgroundColor: "#000000" }}>
          <img src={KNORIA_LOGO_URL} alt="KNORIA" className="w-full block" style={{ display: "block" }} />
        </div>

        <nav ref={desktopNavRef} onScroll={handleSidebarScroll} className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="desktop-nav">
          {renderSidebarSearchToolbar()}
          {(lcSearch ? filteredPillars : pillars).map((p, i) => renderPillarBlock(p, i, !!lcSearch))}
          {lcSearch && filteredPillars.length === 0 && (
            <div className="text-center py-6 text-sm text-zinc-400" data-testid="menu-search-empty">
              {lang === "fr" ? "Aucun résultat" : "No results"}
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-border">
          {/* Eric (2026-02) — Badge « validators actifs » (admin/founder
              only). Affiche discrètement le nombre de sentinelles de build
              qui protègent l'app (menu, react-hooks, fr-accents). Le tooltip
              détaille chaque validateur. Cf. scripts/validate-*.js. */}
          {canAccessAdmin(user) && sidebarOpen && (
            <div
              className="mb-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 text-[11px] font-medium cursor-help select-none"
              data-testid="sidebar-validators-badge"
              title={lang === "fr"
                ? "3 sentinelles de build actives (bloquent tout déploiement en cas de régression) :\n\n" +
                  "• validate-menu.js — Empêche les crash React #130 (icônes manquantes)\n" +
                  "• validate-react-hooks.js — Vérifie les hooks conditionnels\n" +
                  "• validate-fr-accents.js — Bloque les accents FR manquants (BOGUE 531)\n\n" +
                  "Voir /app/frontend/scripts/validate-*.js"
                : "3 build sentinels active (they block any deployment on regression):\n\n" +
                  "• validate-menu.js — Prevents React #130 crashes (missing icons)\n" +
                  "• validate-react-hooks.js — Checks conditional hooks\n" +
                  "• validate-fr-accents.js — Blocks missing French accents (BUG 531)\n\n" +
                  "See /app/frontend/scripts/validate-*.js"
              }
            >
              <ShieldCheck size={12} weight="fill" className="flex-shrink-0" />
              <span className="truncate">{lang === "fr" ? "3 sentinelles actives" : "3 active sentinels"}</span>
            </div>
          )}
          {canAccessAdmin(user) && !sidebarOpen && (
            <div
              className="mb-2 flex items-center justify-center w-full h-6 rounded-md bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 cursor-help"
              data-testid="sidebar-validators-badge-collapsed"
              title={lang === "fr"
                ? "3 sentinelles de build actives (menu, react-hooks, fr-accents)"
                : "3 build sentinels active (menu, react-hooks, fr-accents)"}
            >
              <ShieldCheck size={12} weight="fill" />
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-sm transition-colors"
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <CaretLeft size={18} /> : <CaretRight size={18} />}
            {sidebarOpen && <span className="text-sm">{t("collapse")}</span>}
          </button>
        </div>

        <div className={`p-3 border-t border-border ${sidebarOpen ? "" : "flex justify-center"}`}>
          {/* Vincent B871 (Eric 2026-02-14 SPEC STRICK PROD) — Badge
              état déploiement visible aux admins/founders uniquement.
              Position : au-dessus du bloc user (avatar + email) pour
              qu'un admin le voie dès qu'il ouvre le dashboard. */}
          {sidebarOpen && (user?.role === "admin" || user?.is_founder || user?.role === "platform_admin") && (
            <div className="mb-2" data-testid="desktop-deploy-status-slot">
              <DeployStatusBadge />
            </div>
          )}
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm truncate">{user?.name}</span>
                  {user?.display_role && (
                    <span className={`flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded ${user?.is_founder ? 'bg-cyan-100 text-cyan-900 border border-cyan-300' : 'bg-amber-100 text-amber-900'}`} data-testid="desktop-display-role">
                      {user.display_role}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
              </div>
              <button
                onClick={() => navigate("/dashboard/preferences")}
                className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-sm"
                data-testid="desktop-preferences-btn"
                title={lang === "fr" ? "Préférences" : "Preferences"}
                aria-label={lang === "fr" ? "Préférences" : "Preferences"}
              >
                <GearSix size={18} />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-sm"
                data-testid="desktop-logout-btn"
              >
                <SignOut size={18} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <button
                onClick={() => navigate("/dashboard/preferences")}
                className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-sm"
                data-testid="desktop-preferences-btn-collapsed"
                title={lang === "fr" ? "Préférences" : "Preferences"}
                aria-label={lang === "fr" ? "Préférences" : "Preferences"}
              >
                <GearSix size={18} />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-sm"
                data-testid="desktop-logout-btn-collapsed"
              >
                <SignOut size={18} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Desktop header */}
      <header
        className={`hidden lg:flex fixed right-0 h-14 glass-header border-b border-zinc-200 z-30 items-center justify-between px-6 transition-all duration-200 ${
          sidebarOpen ? "left-60" : "left-16"
        } ${user?.is_simulating ? "top-8" : "top-0"}`}
      >
        <div className="flex items-center gap-4">
          <GlobalSearch />
          <OrgSwitcher />
          <RoleSwitcher />
          {/* Eric 2026-06-25 (iter118 B3) — migré vers canAccessAdmin. On
              conserve le check explicite `real_is_founder` pour que les
              founders impersonnés (RoleSwitcher) voient toujours la démo. */}
          {(canAccessAdmin(user) || user?.real_is_founder) && (
            <button
              onClick={() => navigate("/demo")}
              className="hidden sm:flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-bold transition-all bg-gradient-to-r from-amber-400 via-pink-500 to-violet-600 text-white hover:opacity-90 shadow-md hover:shadow-lg"
              data-testid="btn-launch-demo"
              title={lang === "fr" ? "Démo guidée - 3 minutes (pour prospects FLHQ)" : "Guided demo - 3 minutes (for FLHQ prospects)"}
            >
              <Sparkle size={14} weight="fill" />
              <span>{lang === "fr" ? "Démo" : "Demo"}</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => switchLang(lang === "fr" ? "en" : "fr")}
            className="h-8 px-2.5 flex items-center gap-1.5 hover:bg-secondary rounded-lg transition-colors text-xs font-semibold text-muted-foreground"
            data-testid="lang-toggle-btn"
          >
            <span className={lang === "fr" ? "text-primary" : ""}>FR</span>
            <span className="text-muted-foreground/30">|</span>
            <span className={lang === "en" ? "text-primary" : ""}>EN</span>
          </button>
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="theme-toggle-btn"
            title={lang === "fr" ? (theme === "dark" ? "Mode clair" : "Mode sombre") : (theme === "dark" ? "Light mode" : "Dark mode")}
          >
            {theme === "dark" ? <Sun size={20} className="text-amber-400" /> : <Moon size={20} className="text-muted-foreground" />}
          </button>
          <NotificationBell />
          <button
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="settings-btn"
            onClick={() => navigate("/dashboard/settings")}
          >
            <GearSix size={20} className="text-muted-foreground" />
          </button>
        </div>
      </header>

      {/* Main content */}
      <main
        className={`min-h-screen transition-all duration-200 overflow-x-hidden ${
          sidebarOpen ? "lg:pl-60" : "lg:pl-16"
        } ${user?.is_simulating ? "pt-[5.5rem] lg:pt-[5.5rem]" : "pt-14 lg:pt-14"}`}
      >
        {/* Vincent B556 (Eric 2026-07-25) — L'ancien bandeau simulation
            « sticky top-14 z-20 » situé ICI a été DÉPLACÉ tout en haut de la
            fenêtre (fixed top-0 z-[60] au début du composant), et le décalage
            du contenu (pt-14 → pt-[5.5rem] = 88px) compense la présence
            simultanée du bandeau (32px) + du header (56px). */}
        <div className="p-4 lg:p-6 max-w-full overflow-x-hidden">
          {/* B1109 (Eric 2026-09-08) — La bannière budget LLM n'est plus
              rendue ici : les deux bannières héritées (B800 barre collée en
              haut, B881 encadré dans la page) sont fusionnées en une seule,
              montée une fois pour toute l'application dans App.js. */}
          {/* Vincent bug #94 (2026-05-08) — Bouton « Retour » universel pour toutes les pages
              non directement accessibles depuis le menu de gauche. La liste menuPaths est
              construite depuis menuConfig (cf. renderMenuItem) et exclut les routes-tiroirs. */}
          {/* Tâche 1148 — Bandeau « incident silencieux » en haut de toutes
              les pages : le signalement ne passe PAS par courriel (il serait
              coupé par la même panne), il doit donc se voir partout. */}
          <SilentIncidentBanner />
          {/* Tâche 1318 — Bandeau « clé IA refusée » en haut de toutes les
              pages, pour concepteur / admin / fondateur : sans lui, on relance
              des générations qui échouent sans savoir pourquoi. */}
          <AiKeyFailureBanner />
          <DashboardBackButtonInjector />
          <RouteErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </RouteErrorBoundary>
          {/* Tâche 1287 — Barre de recherche d'actions (Ctrl+K), montée hors
              du bandeau desktop pour rester disponible sur toutes les tailles. */}
          <CommandPalette lang={lang} />
          {/* Vincent XLSX 2026-07-15 Row 483 (Eric 2026-07-16 SPEC STRICK) —
              Watermark de version en pied de page. Permet à Vincent de savoir
              instantanément quelle build est déployée avant de tester. Le build
              date est injecté au build-time via REACT_APP_BUILD_DATE ; sinon on
              affiche une chaîne statique. Deployé le : à mettre à jour lors du
              déploiement (feature « Deploy » d'Emergent). */}
          <footer
            className="mt-10 pt-6 pb-4 border-t border-zinc-200 text-center text-[10px] text-zinc-600 font-mono select-all"
            data-testid="version-watermark-footer"
          >
            {/* Vincent XLSX 2026-07-20 Row 501 (Eric SPEC STRICK) — Les dates
                proviennent de REACT_APP_BUILD_DATE et REACT_APP_DEPLOY_DATE
                injectées au build par un script pré-build (voir
                scripts/inject-build-dates.js). Si absent, on affiche « inconnue »
                pour que Vincent sache immédiatement qu'il manque une variable. */}
            {/* Tâche 1356 (Eric 2026-06-30) — Le pied de page restait en
                français dans la version anglaise. */}
            {lang === "fr"
              ? `Version : ${process.env.REACT_APP_BUILD_DATE || "inconnue"} — Déployé le : ${process.env.REACT_APP_DEPLOY_DATE || "inconnue"}`
              : `Version: ${process.env.REACT_APP_BUILD_DATE || "unknown"} — Deployed on: ${process.env.REACT_APP_DEPLOY_DATE || "unknown"}`}
          </footer>
        </div>
      </main>
      {/* Vincent #116 (2026-05-12) — Widget "Demander à Knoria" retiré du bas-droit (l'item est dans le menu de gauche). */}
    </div>
  );
}
